import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Chip, IconButton, TextField, InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Pagination from '../../shared/Pagination';
import './Index.css';

const RoleIndex = () => {
  const { token, companyId, isAuthenticated, logout, permissions } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const hasEditAdministrationPermission = permissions.includes('Edit Administration');
  const hasDeleteAdministrationPermission = permissions.includes('Delete Administration');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    loadRoles();
  }, [isAuthenticated, token]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await fetchData('RoleApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setData(response.response_data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await deleteData('RoleApi', `${id}`, token);
        loadRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        alert('Failed to delete role');
      }
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateData('RoleApi', `${id}`, token);
      loadRoles();
    } catch (error) {
      console.error('Error activating role:', error);
      alert('Failed to activate role');
    }
  };

  const filteredData = data.filter((item) =>
    (item.Name && item.Name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="role-index-wrapper">
      {/* Header */}
      <div className="role-index-header">
        <div className="header-left">
          <h1 className="index-title">Roles</h1>
          <p className="index-subtitle">Manage user roles and permissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <TextField
          placeholder="Search by role name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <CircularProgress />
            <p>Loading roles...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔐</div>
            <h3>No Roles Found</h3>
            <p>No roles match your search criteria</p>
          </div>
        ) : (
          <TableContainer component={Paper} className="modern-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className="table-header">Sl No</TableCell>
                  <TableCell className="table-header">Role Name</TableCell>
                  <TableCell className="table-header">Status</TableCell>
                  <TableCell className="table-header" align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.Id} className="table-row">
                    <TableCell className="table-cell">
                      <span className="serial-number">{startIndex + index + 1}</span>
                    </TableCell>
                    <TableCell className="table-cell">
                      <span className="role-name">{item.Name}</span>
                    </TableCell>
                    <TableCell className="table-cell">
                      <Chip
                        label={item.IsActive ? 'Active' : 'Inactive'}
                        color={item.IsActive ? 'success' : 'default'}
                        size="small"
                        className="status-chip"
                      />
                    </TableCell>
                    <TableCell className="table-cell" align="center">
                      <div className="action-buttons">
                        {hasEditAdministrationPermission && (
                          <IconButton
                            size="small"
                            className="action-btn edit-btn"
                            component={Link}
                            to={`/roleDetail/${item.Id}`}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {hasDeleteAdministrationPermission && (
                          <IconButton
                            size="small"
                            className={item.IsActive ? "action-btn delete-btn" : "action-btn activate-btn"}
                            onClick={() => (item.IsActive ? handleDelete(item.Id) : handleActivate(item.Id))}
                            title={item.IsActive ? "Delete" : "Activate"}
                          >
                            {item.IsActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredData.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Summary */}
      {!loading && filteredData.length > 0 && (
        <div className="summary-section">
          <span className="summary-text">
            Total: {data.length} roles
          </span>
        </div>
      )}
    </div>
  );
};

export default RoleIndex;

